'use client';

import { createColumnHelper } from '@tanstack/table-core';
import clsx from 'clsx';
import React, { useCallback, useEffect, useState } from 'react';
import { BsToggleOff, BsToggleOn } from 'react-icons/bs';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { useSearchParams } from 'react-router-dom';

import APIGateway from '@/api';
import { Button } from '@/components/button/button';
import CodeSelect from '@/components/codeSelect';
import { Icon } from '@/components/icon/icon';
import { Loader } from '@/components/loader/loader';
import Modal from '@/components/modal/modal';
import { NativeTable } from '@/components/nativeTable/nativeTable';
import { useNativeTable } from '@/components/nativeTable/useNativeTable';
import { buildColumnClassString, ColumnModEnums } from '@/components/table/table';
import { Tabs, TabItem } from '@/components/tabs/tabs';
import { TimezoneToggle } from '@/components/timezoneToggle/timezoneToggle';
import { Heading } from '@/components/typography/heading';
import { useUserQuery } from '@/hooks/queries/useOrg';
import { useAuthContext } from '@/hooks/useAuthContext';
import { useLocalZonePreference } from '@/hooks/useLocalZonePreference';
import { ApiKey, CreateApiKeyRes, GetApiKeysRes } from '@/types/apiKey';
import { isSettingsTab, SettingsTab } from '@/utils/secretsLink';
import { OutputFormatEnum, TimeFormatter, TimeSourceTypeEnum } from '@/utils/timeFormatter';

import { SecretsPanel } from './secretsPanel/secretsPanel';
import styles from './page.module.scss';

// Search-param keys owned by the SecretsPanel filters. Cleared when the user
// navigates away from the Secrets tab so they don't leak into other tabs.
const SECRET_FILTER_KEYS = [ 'type', 'name', 'agent' ] as const;

const SETTINGS_TABS: TabItem<SettingsTab>[] = [
  { value: 'apiKeys', label: 'API Keys', 'data-testid': 'settings-tab-apiKeys' },
  { value: 'secrets', label: 'Secrets', 'data-testid': 'settings-tab-secrets' },
  { value: 'settings', label: 'Settings', 'data-testid': 'settings-tab-settings' }
];


export const dynamic = 'force-dynamic';

const columnHelper = createColumnHelper<ApiKey>();

interface CreateKeyModalProps {
  error?: string;
  newDesc: string;
  newName: string;
  onCancel: () => void;
  onMakeKey: () => void;
  setNewDesc: ( value: string ) => void;
  setNewName: ( value: string ) => void;
}

function CreateKeyModal( {
  error,
  newDesc: _newDesc,
  newName,
  onCancel,
  onMakeKey,
  setNewDesc: _setNewDesc,
  setNewName
}: CreateKeyModalProps ) {
  const handleSubmit = ( e: React.FormEvent ) => {
    e.preventDefault();
    onMakeKey();
  };

  return (
    <Modal>
      <form onSubmit={ handleSubmit }>
        <Heading as="h2" size="md">Create New API Key</Heading>
        <div className={ clsx( 'layout__col', 'pad' ) }>
          <label className='auth_flow_label' htmlFor='name'>Name</label><br />
          <input
            className='auth_flow_input'
            id='name'
            onChange={ ( event ) => setNewName( event.target.value ) }
            type='text'
            value={ newName }
            required
          /><br />
        </div>
        { error && (
          <div className={ clsx( 'layout__col', 'pad' ) } style={ { color: 'red' } }>
            { error }
          </div>
        ) }
        <div className={ clsx( 'layout__row', 'pad', 'flex_right' ) }>
          <Button variant='secondary' size='sm' onClick={ onCancel }>Cancel</Button>
          <Button variant='primary' size='sm' type='submit' disabled={ !newName.length }>Create</Button>
        </div>
      </form>
    </Modal>
  );
}

interface DeleteConfirmModalProps {
  keyName: string;
  onDeleteCancel: () => void;
  onDeleteConfirm: () => void;
}

function DeleteConfirmModal( { keyName, onDeleteCancel, onDeleteConfirm }: DeleteConfirmModalProps ) {
  return (
    <Modal>
      <Heading as="h2" size="md">Confirm Delete</Heading>
      <div className={ clsx( 'layout__col', 'pad' ) }>
        <p>Are you sure you want to delete the key <strong>{ keyName }</strong>?</p>
        <p>This action cannot be undone.</p>
      </div>
      <div className={ clsx( 'layout__row', 'pad', 'flex_right' ) }>
        <Button variant='secondary' size='sm' onClick={ onDeleteCancel }>Cancel</Button>
        <Button variant='destructive' size='sm' onClick={ onDeleteConfirm }>
          Delete
        </Button>
      </div>
    </Modal>
  );
}

interface ShowKeyModalProps {
  apiKey: string;
  onClose: () => void;
}

function ShowKeyModal( { apiKey, onClose }: ShowKeyModalProps ) {
  return (
    <Modal>
      <Heading as="h2" size="md">Key</Heading>
      <div className={ clsx( 'layout__row', 'pad' ) }>
        <div>Your Key is: <CodeSelect value={ apiKey }/></div>
      </div>
      <div className={ clsx( 'layout__row', 'pad' ) }>
        <div>Please save your key, after you close this screen you will not be able to access it again.</div>
      </div>
      <div className={ clsx( 'layout__row', 'pad', 'flex_right' ) }>
        <Button variant='primary' size='sm' onClick={ onClose }>Close</Button>
      </div>
    </Modal>
  );
}

interface ApiKeysPanelProps {
  apiKey: string;
  apiKeys: ApiKey[];
  createError: string;
  deleteKeyName: string;
  isAdmin: boolean;
  isLoading: boolean;
  newDesc: string;
  newName: string;
  onCancel: () => void;
  onCloseKey: () => void;
  onCreate: () => void;
  onDeleteCancel: () => void;
  onDeleteConfirm: () => void;
  onKeyDelete: ( index: number ) => void;
  onKeyDisable: ( index: number ) => void;
  onKeyEnable: ( index: number ) => void;
  onMakeKey: () => void;
  setNewDesc: ( value: string ) => void;
  setNewName: ( value: string ) => void;
  showCreate: boolean;
  showDeleteConfirm: boolean;
  showKey: boolean;
}

function ApiKeysPanel( {
  apiKey,
  apiKeys,
  createError,
  deleteKeyName,
  isAdmin,
  isLoading,
  newDesc,
  newName,
  onCancel,
  onCloseKey,
  onCreate,
  onDeleteCancel,
  onDeleteConfirm,
  onKeyDelete,
  onKeyDisable,
  onKeyEnable,
  onMakeKey,
  setNewDesc,
  setNewName,
  showCreate,
  showDeleteConfirm,
  showKey
}: ApiKeysPanelProps ) {
  const [ useLocalZone ] = useLocalZonePreference();

  const columns = React.useMemo( () => [
    columnHelper.accessor( 'key_name', {
      header: 'Key',
      size: 200,
      cell: info => info.getValue(),
      meta: {
        classStr: buildColumnClassString( { width: 20, styles: ColumnModEnums.NO_OVERFLOW } )
      }
    } ),
    columnHelper.accessor( 'key_alias', {
      header: 'Name',
      size: 150,
      cell: info => info.getValue(),
      meta: {
        classStr: buildColumnClassString( { width: 15, styles: ColumnModEnums.NO_OVERFLOW } )
      }
    } ),
    ...( isAdmin ? [
      columnHelper.accessor( 'owner_email', {
        header: 'Owner',
        size: 250,
        cell: info => info.getValue() || '-',
        meta: {
          classStr: buildColumnClassString( { width: 25, styles: ColumnModEnums.NO_OVERFLOW } )
        }
      } )
    ] : [] ),
    columnHelper.accessor( 'created_at', {
      header: 'Created At',
      size: 200,
      cell: info => (
        <TimeFormatter
          key={ info.row.original.id }
          sourceType={ TimeSourceTypeEnum.ISO }
          value={ info.getValue() }
          outputFormat={ OutputFormatEnum.STRING_SINGLE }
          opts={ { useLocalZone } }
        />
      ),
      meta: {
        classStr: buildColumnClassString( { width: 20, styles: ColumnModEnums.NO_OVERFLOW } )
      }
    } ),
    columnHelper.accessor( 'enabled', {
      header: 'Enabled',
      size: 100,
      cell: info => {
        const apiKey = info.row.original;
        const index = info.row.index;
        return apiKey.enabled ? (
          // Key is enabled, clicking will disable the key
          <span
            data-testid={ `disable-key-${ index }` }
            onClick={ () => onKeyDisable( index ) }
            className='table_actions_item disable_button'
            title="Click to disable key"
          >
            <Icon size='lg' icon={ BsToggleOn } className={ clsx( styles.button_spacing ) }/>Enabled
          </span>
        ) : (
          // Key is disabled, clicking will enable the key
          <span
            data-testid={ `enable-key-${ index }` }
            onClick={ () => onKeyEnable( index ) }
            className='table_actions_item'
            title="Click to enable key"
          >
            <Icon size='lg' icon={ BsToggleOff } className={ clsx( styles.button_spacing ) }/>Disabled
          </span>
        );
      },
      meta: {
        classStr: buildColumnClassString( { width: 10, styles: ColumnModEnums.CENTER } )
      }
    } ),
    columnHelper.display( {
      id: 'delete',
      header: 'Delete',
      size: 100,
      cell: info => {
        const index = info.row.index;
        return (
          <span
            data-testid={ `delete-key-${ index }` }
            onClick={ () => onKeyDelete( index ) }
            className='table_actions_item'
            title="Permanently delete key"
          >
            <FiTrash2 />
          </span>
        );
      },
      meta: {
        classStr: buildColumnClassString( { width: 10, styles: ColumnModEnums.CENTER } )
      }
    } )
  ], [ isAdmin, onKeyDisable, onKeyEnable, onKeyDelete, useLocalZone ] );

  const table = useNativeTable<ApiKey>( {
    data: apiKeys,
    columns,
    enableSorting: false
  } );

  if ( isLoading ) {
    return <Loader />;
  }

  return (
    <>
      {
        showCreate && (
          <CreateKeyModal
            error={ createError }
            newDesc={ newDesc }
            newName={ newName }
            onCancel={ onCancel }
            onMakeKey={ onMakeKey }
            setNewDesc={ setNewDesc }
            setNewName={ setNewName }
          />
        )
      }
      {
        showKey && (
          <ShowKeyModal
            apiKey={ apiKey }
            onClose={ onCloseKey }
          />
        )
      }
      {
        showDeleteConfirm && (
          <DeleteConfirmModal
            keyName={ deleteKeyName }
            onDeleteCancel={ onDeleteCancel }
            onDeleteConfirm={ onDeleteConfirm }
          />
        )
      }

      <div className={ clsx( 'layout__row', 'pad' ) }>
        <Heading as="h2" size="lg">Keys</Heading>
        <Button variant='primary' icon={ <Icon icon={ FiPlus } /> } onClick={ onCreate }>Create Key</Button>
      </div>
      <div className={ clsx( 'layout__row', 'pad' ) }>API keys provide programmatic access to the platform with the same permissions as the user who created them.</div>
      <NativeTable table={ table } pagination="client" />
    </>
  );
}

export default function SettingsPage() {
  const {
    authClientDomain,
    authClientId,
    authOrgId,
    canAccess,
    logout,
    sessionContext
  } = useAuthContext();

  const { data: currentUser } = useUserQuery( sessionContext?.userId );

  const isAdmin = canAccess( 'can_manage_users' );

  const [ apiKeys, setApiKeys ] = useState<ApiKey[]>( [] );
  const [ createError, setCreateError ] = useState<string>( '' );
  const [ isLoading, setIsLoading ] = useState<boolean>( true );
  const [ isResettingPassword, setIsResettingPassword ] = useState<boolean>( false );
  const [ key, setKey ] = useState<string>( '' );
  const [ keyToDelete, setKeyToDelete ] = useState<number | null>( null );
  const [ newDesc, setNewDesc ] = useState<string>( '' );
  const [ newName, setNewName ] = useState<string>( '' );
  const [ pendingStatus, setPendingStatus ] = useState<boolean>( false );
  const [ resetEmailSent, setResetEmailSent ] = useState<boolean>( false );
  const [ showCreate, setShowCreate ] = useState<boolean>( false );
  const [ showDeleteConfirm, setShowDeleteConfirm ] = useState<boolean>( false );
  const [ showKey, setShowKey ] = useState<boolean>( false );
  const [ showPasswordResetConfirm, setShowPasswordResetConfirm ] = useState<boolean>( false );
  const [ useLocalZone, setUseLocalZone ] = useLocalZonePreference();

  useEffect( () => {
    const fetchData = async() => {
      try {
        if( !sessionContext ) return;

        const apiKeysRes: GetApiKeysRes = await APIGateway.apiKeys.apiKeysGet();
        setApiKeys( apiKeysRes.api_keys );
        setIsLoading( false );
      } catch( e ) {
        console.log( e );
      }
    };

    fetchData();
    setPendingStatus( false );
  }, [ key, pendingStatus, sessionContext ] );

  const onCancel = () => {
    setShowCreate( false );
    setCreateError( '' );
  };
  const onCloseKey = () => setShowKey( false );
  const onCreate = async() => {
    setShowCreate( true );
    setCreateError( '' );
  };

  const onKeyDelete = ( keyIndex: number ) => {
    setKeyToDelete( keyIndex );
    setShowDeleteConfirm( true );
  };

  const onDeleteCancel = () => {
    setShowDeleteConfirm( false );
    setKeyToDelete( null );
  };

  const onDeleteConfirm = async() => {
    setShowDeleteConfirm( false );

    if( !sessionContext || keyToDelete === null ) return;

    const key = apiKeys[keyToDelete];

    try {
      const apiKeyRes = await APIGateway.apiKeys.apiKeyDelete( key.id );

      if ( apiKeyRes ) {
        setPendingStatus( true );
      }
      setKeyToDelete( null );
    } catch( e ) {
      console.error( 'Error deleting key:', e );
      setKeyToDelete( null );
      return;
    }
  };

  const onKeyDisable = async( keyIndex: number ) => {
    if( !sessionContext || keyIndex === null ) return;

    const key = apiKeys[keyIndex];

    try {
      const apiKeyRes = await APIGateway.apiKeys.apiKeyDisable( key.id );

      if ( apiKeyRes ) {
        setPendingStatus( true );
      }
    } catch( e ) {
      console.error( 'Error diaabling key:', e );
      return;
    }
  };

  const onKeyEnable = async( keyIndex: number ) => {
    if( !sessionContext || keyIndex === null ) return;

    const key = apiKeys[keyIndex];

    try {
      const apiKeyRes = await APIGateway.apiKeys.apiKeyEnable( key.id );

      if ( apiKeyRes ) {
        setPendingStatus( true );
      }
    } catch( e ) {
      console.error( 'Error enabling key:', e );
      return;
    }
  };

  const onMakeKey = async() => {
    if ( !sessionContext ) return;

    setCreateError( '' );

    try {
      const apiKeyRes: CreateApiKeyRes = await APIGateway.apiKeys.apiKeyCreate( newName );
      setKey( apiKeyRes.key );
      setNewName( '' );
      setShowCreate( false );
      setShowKey( true );
      setPendingStatus( true );
    } catch( error ) {
      // Display error in modal, don't close it
      setCreateError( error instanceof Error ? error.message : 'Failed to create API key' );
    }
  };

  const canResetPassword = sessionContext && authClientDomain && authClientId;

  const onPasswordResetClick = () => setShowPasswordResetConfirm( true );

  const onPasswordResetCancel = () => setShowPasswordResetConfirm( false );

  const onPasswordResetConfirm = async() => {
    setShowPasswordResetConfirm( false );

    if ( !canResetPassword || !currentUser?.email ) return;

    setIsResettingPassword( true );

    try {
      await APIGateway.auth.passwordResetRequest( {
        authClientDomain,
        authClientId,
        authOrgId,
        email: currentUser.email
      } );
      setResetEmailSent( true );

      // Wait a moment to show the success message before logging out
      setTimeout( async() => {
        await logout();
      }, 2000 );
    } catch( e ) {
      console.error( 'Password reset failed:', e );
      setIsResettingPassword( false );
    }
  };

  const [ searchParams, setSearchParams ] = useSearchParams();
  const tabParam = searchParams.get( 'tab' );
  const activeTab: SettingsTab = isSettingsTab( tabParam ) ? tabParam : 'apiKeys';

  const goToTab = useCallback( ( next: SettingsTab ) => {
    setSearchParams( ( prev ) => {
      const params = new URLSearchParams( prev );
      params.set( 'tab', next );
      if ( next !== 'secrets' ) {
        SECRET_FILTER_KEYS.forEach( ( key ) => params.delete( key ) );
      }
      return params;
    } );
  }, [ setSearchParams ] );

  return (
    <div className='page'>
      <Tabs items={ SETTINGS_TABS } active={ activeTab } onChange={ goToTab } data-testid="settings-tabs" />

      <div className={ styles.tabContent }>
        {activeTab === 'apiKeys' && (
          <ApiKeysPanel
            isLoading={ isLoading }
            showCreate={ showCreate }
            showKey={ showKey }
            showDeleteConfirm={ showDeleteConfirm }
            apiKeys={ apiKeys }
            apiKey={ key }
            deleteKeyName={ keyToDelete !== null ? apiKeys[keyToDelete]?.key_alias : '' }
            newName={ newName }
            newDesc={ newDesc }
            isAdmin={ isAdmin }
            createError={ createError }
            setNewName={ setNewName }
            setNewDesc={ setNewDesc }
            onCancel={ onCancel }
            onMakeKey={ onMakeKey }
            onCreate={ onCreate }
            onCloseKey={ onCloseKey }
            onKeyDelete={ onKeyDelete }
            onDeleteCancel={ onDeleteCancel }
            onDeleteConfirm={ onDeleteConfirm }
            onKeyDisable={ onKeyDisable }
            onKeyEnable={ onKeyEnable }
          />
        )}

        {activeTab === 'secrets' && (
          <SecretsPanel />
        )}

        {activeTab === 'settings' && (
          isLoading ? (
            <Loader />
          ) : (
            <>
              <div className={ clsx( 'layout__row', 'pad' ) }>
                <Heading as="h2" size="lg">Settings</Heading>
              </div>
              <div className={ clsx( 'layout__row', 'pad' ) }>
                <div>
                  <Heading as="h3" size="md">Timezone Display</Heading>
                  <p>Choose how timestamps are displayed throughout the application.</p>
                  <TimezoneToggle
                    useLocalZone={ useLocalZone }
                    setUseLocalZone={ setUseLocalZone }
                  />
                </div>
              </div>
              { canResetPassword && (
                <div className={ clsx( 'layout__row', 'pad' ) }>
                  <div>
                    { showPasswordResetConfirm && (
                      <Modal>
                        <Heading as="h2" size="md">Confirm Password Reset</Heading>
                        <div className={ clsx( 'layout__col', 'pad' ) }>
                          <p>Are you sure you want to request a password reset email?</p>
                          <p>A reset link will be sent to <strong>{ currentUser?.email }</strong> and you will be logged out.</p>
                        </div>
                        <div className={ clsx( 'layout__row', 'pad', 'flex_right' ) }>
                          <Button variant='secondary' size='sm' onClick={ onPasswordResetCancel }>Cancel</Button>
                          <Button variant='primary' size='sm' onClick={ onPasswordResetConfirm }>Confirm</Button>
                        </div>
                      </Modal>
                    ) }
                    <Heading as="h3" size="md">Password</Heading>
                    <p>Request a password reset email to change your password.</p>
                    { resetEmailSent ? (
                      <div style={ { color: 'green', marginBottom: '10px' } }>
                        Password reset email has been sent to { currentUser?.email }. You will be logged out shortly.
                      </div>
                    ) : null }
                    <Button
                      variant='primary'
                      size='sm'
                      onClick={ onPasswordResetClick }
                      loading={ isResettingPassword }
                      disabled={ isResettingPassword || resetEmailSent }
                    >
                      Request Password Reset
                    </Button>
                  </div>
                </div>
              )}
            </>
          )
        )}
      </div>
    </div>
  );
}
